import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import applyIcon from '../../../../assets/images/applyIcon.svg';
import IconShare from '../../../../assets/images/IconShare.svg';
import tablerIconUserShare from '../../../../assets/images/tablerIconUserShare.svg';
import Notification from '../../../../components/Notification/Notification';
import ButtonWithIcon from '../../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import { copyToClipboard } from '../../../../helpers/commonHelper';
import { IsMobileCustom } from '../../../../helpers/IsMobileCustom';
import { useSocialShare } from '../../../../helpers/socialShareUtils';
import newUserAppUrl from '../../../../routing/newUserApp';
import urls from '../../../../routing/urls';
import { selectCurrentOrganization } from '../../../../selectors/organizationSelectors';
import ShareModal from '../../../Company/ShareModal/ShareModal';
import CandidateInfoModal from '../../CandidateInfoModal/CandidateInfoModal';
import JobShareModal from '../../MobileView/JobShareModal';

import styles from './ButtonMenu.module.scss';

const ButtonMenu = ({
  isExternalLink,
  isCandidateApplied,
  opening,
  handleApplyJob,
  showModal,
  setShowModal,
  isPublic,
  disableReferButton,
  referralProvider,
}) => {
  const { t } = useTranslation('common');
  const mobile = IsMobileCustom();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const type = 'job';
  const jobUuid = opening?.uuid;
  const text = t('share.shareWithCommunity');

  const handleSocialShare = useSocialShare(jobUuid, opening, type);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const organization = useSelector(selectCurrentOrganization);

  const handleCopyLink = () => {
    if (!jobUuid) {
      Notification.error(t('validation.noJobIdAvailable'));
      return;
    }

    const link = newUserAppUrl + urls.jobDetail.replace(':jobId', jobUuid);

    copyToClipboard(link, type);
  };

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  return (
    <div className={styles.buttonContainer}>
      <ButtonWithIcon
        img={applyIcon}
        disabled={isCandidateApplied}
        text={isCandidateApplied ? t('buttons.applied') : t('buttons.apply')}
        imgClassName="btn-icon-36"
        textClassName="btn-icon-text-primary-40"
        variant="secondary-36"
        onClick={handleApplyJob}
        isExternalLink={isExternalLink}
      />
      <ButtonWithIcon
        img={tablerIconUserShare}
        text={t('buttons.refer')}
        imgClassName="btn-icon-36"
        textClassName="btn-icon-text-tertiary-40"
        variant="tertiary-36"
        onClick={(e) => {
          e.stopPropagation();

          const url = pathname.includes('company') || isPublic ? urls.companyReferral : urls.refer;
          navigate(
            `${url.replace(
              ':companyName',
              organization?.general_setting_identifier || opening.general_setting_identifier
            )}?refer_for=${opening.uuid}`
          );
        }}
        disabled={!opening?.referral_link || disableReferButton || referralProvider}
      />
      <ButtonWithIcon img={IconShare} variant="only-icon-tertiary-36" onClick={handleModalOpen} />
      {mobile ? (
        <JobShareModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          opening={opening}
          handleSocialShare={handleSocialShare}
          onCopyLink={handleCopyLink}
          text={text}
          type={type}
        />
      ) : (
        <ShareModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          company={opening}
          handleSocialShare={handleSocialShare}
          onCopyLink={handleCopyLink}
        />
      )}

      {showModal && (
        <CandidateInfoModal
          showModal={showModal}
          setShowModal={setShowModal}
          organization={opening?.organization_name}
          jobTitle={opening?.title}
          job={opening}
          referralProvider={referralProvider}
        />
      )}
    </div>
  );
};

export default ButtonMenu;
